import { useCallback, useState } from 'react';

import { planningCyclesService } from '@/services/demandPlanning/planningCycles.service';
import { supplyService } from '@/services/supplyService';

import type { PublishFlowStep, PublishFlowStepId, PublishFlowStepStatus } from '../types/consensus.types';
import { PUBLISH_FLOW_STEP_IDS } from '../types/consensus.types';
import { refreshSupplyAfterDemandPublish } from '../utils/refreshSupplyAfterDemandPublish';

function initialSteps(): PublishFlowStep[] {
  return PUBLISH_FLOW_STEP_IDS.map((id) => ({ id, status: 'pending' as const }));
}

function setStepStatus(
  steps: PublishFlowStep[],
  id: PublishFlowStepId,
  status: PublishFlowStepStatus,
  errorMessage?: string,
): PublishFlowStep[] {
  return steps.map((step) =>
    step.id === id ? { ...step, status, errorMessage: errorMessage ?? step.errorMessage } : step,
  );
}

export type PublishDemandPlanModalMode = 'confirm' | 'progress';

export interface PublishDemandPlanRequest {
  scenarioId: number | null;
  republish: boolean;
  scenarioName: string;
}

export function usePublishDemandPlanFlow(options: {
  cycleId: number | null;
  warehouseId: number | null;
  onSuccess: () => void;
}) {
  const { cycleId, warehouseId, onSuccess } = options;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<PublishDemandPlanModalMode>('confirm');
  const [steps, setSteps] = useState<PublishFlowStep[]>(initialSteps);
  const [isRunning, setIsRunning] = useState(false);
  const [publishSucceeded, setPublishSucceeded] = useState(false);
  const [flowComplete, setFlowComplete] = useState(false);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<PublishDemandPlanRequest | null>(null);

  const closeModal = useCallback(() => {
    if (isRunning) return;
    setModalOpen(false);
    setModalMode('confirm');
    setSteps(initialSteps());
    setPublishSucceeded(false);
    setFlowComplete(false);
    setPartialError(null);
    setPendingRequest(null);
  }, [isRunning]);

  const requestPublish = useCallback((request: PublishDemandPlanRequest) => {
    setPendingRequest(request);
    setModalMode('confirm');
    setSteps(initialSteps());
    setPublishSucceeded(false);
    setFlowComplete(false);
    setPartialError(null);
    setModalOpen(true);
  }, []);

  const runFlow = useCallback(
    async (request: PublishDemandPlanRequest, skipPublish: boolean) => {
      if (cycleId == null) return;

      setIsRunning(true);
      setModalMode('progress');
      setPartialError(null);
      setFlowComplete(false);

      let nextSteps = initialSteps();
      if (skipPublish) {
        nextSteps = setStepStatus(nextSteps, 'validate', 'skipped');
        nextSteps = setStepStatus(nextSteps, 'publish', 'done');
        setPublishSucceeded(true);
      } else {
        nextSteps = setStepStatus(nextSteps, 'validate', 'running');
        setSteps(nextSteps);
        nextSteps = setStepStatus(nextSteps, 'validate', 'done');
        nextSteps = setStepStatus(nextSteps, 'publish', 'running');
        setSteps(nextSteps);

        try {
          await planningCyclesService.publishDemandPlan(cycleId, request.scenarioId);
          nextSteps = setStepStatus(nextSteps, 'publish', 'done');
          setSteps(nextSteps);
          setPublishSucceeded(true);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Publish failed';
          nextSteps = setStepStatus(nextSteps, 'publish', 'error', message);
          setSteps(nextSteps);
          setPartialError(message);
          setIsRunning(false);
          return;
        }
      }

      nextSteps = setStepStatus(nextSteps, 'replenishment', 'running');
      setSteps(nextSteps);

      try {
        await supplyService.recalculateOrganization({ force: true });
        nextSteps = setStepStatus(nextSteps, 'replenishment', 'done');
        setSteps(nextSteps);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Replenishment failed';
        nextSteps = setStepStatus(nextSteps, 'replenishment', 'error', message);
        setSteps(nextSteps);
        setPartialError(message);
        setIsRunning(false);
        return;
      }

      nextSteps = setStepStatus(nextSteps, 'supply_cache', 'running');
      setSteps(nextSteps);

      try {
        await refreshSupplyAfterDemandPublish(warehouseId);
        nextSteps = setStepStatus(nextSteps, 'supply_cache', 'done');
        setSteps(nextSteps);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Supply cache refresh failed';
        nextSteps = setStepStatus(nextSteps, 'supply_cache', 'error', message);
        setSteps(nextSteps);
        setPartialError(message);
        setIsRunning(false);
        return;
      }

      setFlowComplete(true);
      setIsRunning(false);
      onSuccess();
    },
    [cycleId, warehouseId, onSuccess],
  );

  const confirmPublish = useCallback(async () => {
    if (!pendingRequest) return;
    await runFlow(pendingRequest, false);
  }, [pendingRequest, runFlow]);

  const retryReplenishment = useCallback(async () => {
    if (!pendingRequest || !publishSucceeded) return;
    setPartialError(null);
    setFlowComplete(false);
    const resetSteps = initialSteps().map((step) =>
      step.id === 'publish' || step.id === 'validate'
        ? { ...step, status: 'done' as const }
        : step,
    );
    setSteps(resetSteps);
    await runFlow(pendingRequest, true);
  }, [pendingRequest, publishSucceeded, runFlow]);

  return {
    modalOpen,
    modalMode,
    steps,
    isRunning,
    flowComplete,
    partialError,
    publishSucceeded,
    pendingRequest,
    requestPublish,
    confirmPublish,
    retryReplenishment,
    closeModal,
  };
}
